"use client"

import { useEffect } from "react"
import { useSession } from "next-auth/react"
import { setUserContext, addBreadcrumb, setTag } from "@/lib/sentry"

interface SessionWithUser {
  user: {
    id: string
    email?: string | null
    name?: string | null
    image?: string | null
  }
}

export function PerformanceMonitor() {
  const { data: session } = useSession()

  useEffect(() => {
    // Set user context when session changes
    if (session?.user) {
      setUserContext({
        id: (session.user as SessionWithUser['user']).id,
        email: session.user.email || undefined,
        username: session.user.name || undefined,
      })
    }
  }, [session])

  // removed: duplicate page-view navigation breadcrumbs (Umami handles page views / sessions)

  useEffect(() => {
    // Track performance metrics
    const trackPerformance = () => {
      if (typeof window !== "undefined" && "performance" in window) {
        const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming
        
        if (navigation) {
          setTag("page_load_time", Math.round(navigation.loadEventEnd - navigation.fetchStart).toString())
          setTag("dom_content_loaded", Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart).toString())
          
          addBreadcrumb({
            message: "Page performance metrics",
            category: "performance",
            level: "info",
            data: {
              loadTime: Math.round(navigation.loadEventEnd - navigation.fetchStart),
              domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
              firstPaint: navigation.loadEventEnd - navigation.fetchStart,
            },
          })
        }
      }
    }

    // Track performance after page load
    if (document.readyState === "complete") {
      trackPerformance()
    } else {
      window.addEventListener("load", trackPerformance)
    }

    return () => {
      window.removeEventListener("load", trackPerformance)
    }
  }, [])

  return null // This component doesn't render anything
}
